import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import type { Classification } from '@/features/classifications/api'
import { isOfferable } from '../lib/template-graph'

export const CLASSIFICATION_DND_TYPE = 'application/vs-classification'

/**
 * The template canvas's palette. Where the planning canvas offers the OBJECTS that may sit
 * beneath a main node, this offers CLASSIFICATIONS — a template has no objects to draw, only
 * kinds of thing.
 *
 * Two behaviours differ from the available-nodes list, and both matter:
 *   - it does not shrink. An object can only be on a canvas once; a template can hold four objects
 *     of one class, so a class stays after every drop.
 *   - what is offerable comes from ClassificationRules against what is already drawn, not from a
 *     hierarchy. A class nothing drawn can legally sit above or below is greyed rather than
 *     hidden: "why can I not use this?" deserves an answer, and the answer is the rules.
 */
export function ClassificationPalette({ classifications, rules, drawnClassIds }: {
    classifications: Classification[]
    rules: Set<string>
    drawnClassIds: number[]
}) {
    const { t } = useTranslation()
    const [search, setSearch] = useState('')

    const groups = useMemo(() => {
        const term = search.trim().toLowerCase()
        const matching = classifications
            .filter((c) => Number(c.Active ?? 1) === 1)
            .filter((c) => !term
                || c.Name.toLowerCase().includes(term)
                || String(c.Category || '').toLowerCase().includes(term))
        // Band order, highest first, so the palette reads in the same order as the canvas.
        const sorted = [...matching].sort(
            (a, b) => Number(b.DisplayRank) - Number(a.DisplayRank) || a.Name.localeCompare(b.Name),
        )
        const byCategory = new Map<string, Classification[]>()
        for (const c of sorted) {
            const key = c.Category || t('templates.editor.uncategorised', 'Uncategorised')
            if (!byCategory.has(key)) byCategory.set(key, [])
            byCategory.get(key)!.push(c)
        }
        return [...byCategory.entries()]
    }, [classifications, search, t])

    return (
        <div className="flex min-h-0 flex-col gap-2">
            <div className="relative">
                <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                    className="h-8 pl-7 text-xs"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('templates.editor.searchClasses', 'Search classifications…')}
                />
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                {groups.map(([category, list]) => (
                    <div key={category} className="mb-2">
                        <div className="px-1 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                            {category}
                        </div>
                        {list.map((cls) => {
                            const offerable = isOfferable(rules, drawnClassIds, cls.id)
                            return (
                                <div
                                    key={cls.id}
                                    draggable={offerable}
                                    onDragStart={(e) => {
                                        // The whole classification travels, so the drop needs no lookup.
                                        e.dataTransfer.setData(CLASSIFICATION_DND_TYPE, JSON.stringify(cls))
                                        e.dataTransfer.effectAllowed = 'copy'
                                    }}
                                    title={offerable
                                        ? cls.Description || cls.Name
                                        : t('templates.editor.notOfferable', 'The classification rules do not allow this above or below anything drawn yet.')}
                                    className={`mb-0.5 flex items-center gap-2 rounded border px-2 py-1 text-xs ${
                                        offerable ? 'cursor-grab bg-background hover:bg-muted' : 'cursor-not-allowed opacity-40'
                                    }`}
                                >
                                    <span
                                        className="inline-block h-3 w-3 shrink-0 rounded-sm border"
                                        style={{ background: cls.BackgroundColor || 'transparent' }}
                                    />
                                    <span className="truncate">{cls.Name}</span>
                                    <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">{cls.DisplayRank}</span>
                                </div>
                            )
                        })}
                    </div>
                ))}
                {!groups.length && (
                    <p className="px-1 py-2 text-xs text-muted-foreground">
                        {t('templates.editor.noClasses', 'No classification matches that.')}
                    </p>
                )}
            </div>
        </div>
    )
}
